<script context="module">
  export const DEFAULT_LAYOUT = /** @type { const } */ "default";
</script>

<script>
  // @ts-check
  import { setContext } from "svelte";

  import GridContainer from "../../atoms/GridContainer.svelte";
  import GridDivider from "../../atoms/GridDivider.svelte";
  import Rule from "../../atoms/Rule.svelte";
  import { VALID_LAYOUTS } from "../CardLayout.svelte";
  import {
    SPLIT_LAYOUT_VARIANTS,
    SPLIT_LAYOUT_RIGHT_GRID_COLUMNS
  } from "../../organisms/SplitLayout.svelte";
  import Masonry from "./GridMasonry.svelte";

  /** Primarily used for testing
   * @type {string} */
  export let id = undefined;

  /**
   * The theme of the component using the Rule.
   * @type {import('../../utilities/themes').ThemeName}
   */
  export let theme = "white";

  /** @type {typeof import("svelte").SvelteComponent<unknown>} */
  export let component;

  /** @type {Array<Object>} */
  export let items;

  /** @typedef {SPLIT_LAYOUT_VARIANTS[keyof SPLIT_LAYOUT_VARIANTS]} SPLIT_LAYOUT_VARIANTS_VALUES */
  /**
   * The split layout variant
   * @type  { SPLIT_LAYOUT_VARIANTS_VALUES }
   */
  export let splitLayoutVariant = null;

  /** @typedef {typeof import('../CardLayout.svelte')['VALID_LAYOUTS']} VALID_LAYOUTS*/
  /** @typedef {Pick<VALID_LAYOUTS, 'TwoColumns' | 'ThreeColumns'> } MASONRY_VALID_LAYOUTS*/
  /** @typedef {MASONRY_VALID_LAYOUTS[keyof MASONRY_VALID_LAYOUTS]} MASONRY_VALID_LAYOUTS_VALUES */
  /** @type {MASONRY_VALID_LAYOUTS_VALUES} */
  export let layout;

  /** @type {string} */
  export let containerClass = "";

  /** @typedef {2 | 3} NumberOfColumns */

  /** @typedef {Object} GridConfig
   * @property {NumberOfColumns} numberOfColumns
   * @property {Record<SPLIT_LAYOUT_VARIANTS_VALUES | DEFAULT_LAYOUT, string>} itemClasses
   */

  /** @type {Record<MASONRY_VALID_LAYOUTS_VALUES, GridConfig>} */
  const GRID_LAYOUTS = /** @type {const} **/ ({
    [VALID_LAYOUTS.TwoColumns]: {
      numberOfColumns: 2,
      itemClasses: {
        [SPLIT_LAYOUT_VARIANTS.LAYOUT_50_50]: "col-span-4 md:col-span-6 lg:col-span-3",
        [SPLIT_LAYOUT_VARIANTS.LAYOUT_25_75]: "col-span-4 md:col-span-3 lg:col-span-4",
        [SPLIT_LAYOUT_VARIANTS.LAYOUT_75_25]: "col-span-4 md:col-span-2 lg:col-span-1",
        [DEFAULT_LAYOUT]: "col-span-4 md:col-span-6"
      }
    },
    [VALID_LAYOUTS.ThreeColumns]: {
      numberOfColumns: 3,
      itemClasses: {
        [SPLIT_LAYOUT_VARIANTS.LAYOUT_50_50]: "col-span-4 md:col-span-4 lg:col-span-2",
        [SPLIT_LAYOUT_VARIANTS.LAYOUT_25_75]: "col-span-4 md:col-span-2 lg:col-span-3",
        [SPLIT_LAYOUT_VARIANTS.LAYOUT_75_25]: "col-span-4 md:col-span-1 lg:col-span-1",
        [DEFAULT_LAYOUT]: "col-span-4 md:col-span-6 lg:col-span-4"
      }
    }
  });

  const gridConfig = GRID_LAYOUTS[layout];

  const { numberOfColumns, itemClasses } = gridConfig;

  const gridClasses = itemClasses[splitLayoutVariant || DEFAULT_LAYOUT];
  const numberOfColumnsPerBreakpoint = splitLayoutVariant
    ? /** @type {const} **/ ({ sm: 1, md: numberOfColumns, lg: numberOfColumns })
    : /** @type {const} **/ ({ sm: 1, md: 2, lg: numberOfColumns });

  setContext("CardLayoutMasonry", { id, layout, component });
</script>

<GridContainer {id} {containerClass} variant={splitLayoutVariant ? "bleed" : "regular"}>
  <div class="relative">
    <GridDivider
      subGridColumns={SPLIT_LAYOUT_RIGHT_GRID_COLUMNS[splitLayoutVariant]}
      numberOfColumns={splitLayoutVariant ? numberOfColumns : 2}
      containerClasses="hidden md:block lg:hidden"
      {theme}
    />
    <GridDivider
      subGridColumns={SPLIT_LAYOUT_RIGHT_GRID_COLUMNS[splitLayoutVariant]}
      {numberOfColumns}
      containerClasses="hidden lg:block"
      {theme}
    />
    <Masonry
      {items}
      {numberOfColumnsPerBreakpoint}
      subGridColumns={SPLIT_LAYOUT_RIGHT_GRID_COLUMNS[splitLayoutVariant]}
      gridClass="gap-y-7.5 md:gap-y-15"
    >
      {#each items as item, i}
        <div class={gridClasses}>
          <svelte:component this={component} {...item} />
          {#if i < items.length - 1}
            <div class="pt-7.5 md:hidden">
              <Rule {theme} />
            </div>
          {/if}
        </div>
      {/each}
    </Masonry>
  </div>
</GridContainer>
